import React, { useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import ImmutablePropTypes from 'react-immutable-proptypes'
import _replace from 'lodash/replace'
import _get from 'lodash/get'
import compose from 'recompose/compose'
import { connect as connectRedux } from 'react-redux'
import { getBoundActions } from 'actions'
import { getAuthIsLoggedIn } from 'services/auth'
import { getPrimary } from 'services/languages'
import { List, Map } from 'immutable'
import { getCurrentTime } from 'services/date-time'
import { get as getConfig } from 'config'
import {
  PLAN_SKU_COMPLIMENTARY,
  getPlanSkus,
} from 'services/plans'
import {
  BILLING_PLAN_GRID_LOCATION_CHECKOUT,
} from 'services/plans/utils'
import { useGetPlanGridData, useGetPlanDataBySku } from 'services/plans/hooks'
import { USD } from 'services/currency'
import SherpaMessage from 'components/SherpaMessage'
import PlanCard from 'components/PlanCard'
import Slider from 'components/Slider'
import Button, { TYPES, SIZES } from 'components/Button'
import { H2, H3, HEADING_TYPES } from 'components/Heading'
import { isSinglePageCheckout } from 'services/url'
import { isTaxable } from 'services/user-account'
import {
  getPlanCardData,
  getCurrencyIso,
  onChoosePlanClick,
} from './utils'

const config = getConfig()
const planGraphQLActive = !!_get(config, ['features', 'checkout', 'planGraphQL'])

function PlanCardGrid (props) {
  const {
    plans = Map(),
    user = Map(),
    userAccount = Map(),
    staticText = Map(),
    auth = Map(),
    isAccountChangePlan,
    planGridLocation,
    sliderMode,
    isMobile,
    path,
  } = props
  const userLanguage = getPrimary(user.getIn(['data', 'language']))
  const authJwt = auth.get('jwt')
  const isLoggedIn = getAuthIsLoggedIn(auth)
  const taxableCountry = isTaxable(props)
  let plansList = plans.getIn(['data', 'plans'], List()) || List()
  if (planGraphQLActive) {
    plansList = useGetPlanGridData({
      userLanguage,
      authJwt,
      page: planGridLocation,
      forceRefresh: true,
    })
  }

  let userPlanSku = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'plan', 'planSku'])
  const accountNextPlan = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'nextPlan'])
  let accountNextPlanSku = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'nextPlan', 'planSku'])
  const accountFuturePlanSku = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'futurePlan', 'planSku'])

  // if the user has a complimentary plan, use the next plan sku
  if (userPlanSku === PLAN_SKU_COMPLIMENTARY && accountNextPlan) {
    userPlanSku = accountNextPlanSku
  }

  // if the next plan is a complimentary plan, use the future plan sku
  if (accountNextPlanSku === PLAN_SKU_COMPLIMENTARY && accountFuturePlanSku) {
    accountNextPlanSku = accountFuturePlanSku
  }

  const skus = getPlanSkus(props)
  const gqlUserCurrentPlanData = isAccountChangePlan
    ? useGetPlanDataBySku({
      userLanguage,
      authJwt,
      planSkus: userPlanSku,
      forceRefresh: true,
    })
    : Map()
  const userCurrentPlan = gqlUserCurrentPlanData.get('plans', List()) || List()

  const gqlNextPlanData = isAccountChangePlan && accountNextPlanSku !== userPlanSku
    ? useGetPlanDataBySku({
      userLanguage,
      authJwt,
      planSkus: accountNextPlanSku,
      forceRefresh: true,
    })
    : Map()
  const userNextPlan = gqlNextPlanData.get('plans', List()) || List()
  const currentPlan = List.isList(userCurrentPlan) ? userCurrentPlan.first() || Map() : Map()
  const nextPlan = List.isList(userNextPlan) ? userNextPlan.first() || Map() : Map()
  const selectedPlan = plans.get('selection') || Map()
  const cancelDate = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'cancelDate'])
  const endDate = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'endDate'])
  const today = getCurrentTime()
  const cancellationScheduled = cancelDate && (new Date(endDate) >= today)
  const currencyIso = getCurrencyIso({ plansList, selectedPlan, userAccount }) || USD
  /* eslint-disable no-template-curly-in-string */
  const pricesPlusTax = _replace(staticText.get('pricePlusTax', ''), '${ currencyIso }', currencyIso)

  // Single Page Checkout - slider
  const isSingleCheckoutPage = isSinglePageCheckout(path)
  const startIndexSlide = isMobile && isSingleCheckoutPage ? 0 : 1
  const [activeSlide, setActiveSlide] = useState(startIndexSlide)

  const extraProps = {
    cancellationScheduled,
    useGraphQLData: planGraphQLActive,
    ...(planGraphQLActive && { plansList }),
    ...(planGraphQLActive && { currentPlan }),
  }
  const getPlans = useCallback(() => {
    let newPlansList = plansList.filter((val) => skus.includes(val.get('sku')))
    newPlansList = newPlansList.sort((a, b) => skus.indexOf(a.get('sku')) - skus.indexOf(b.get('sku')))

    return newPlansList
  }, [isLoggedIn, plansList, staticText])

  let plansData = getPlanCardData({
    plans: planGraphQLActive ? plansList : getPlans() || List(),
    currencyIso,
    staticText,
    userLanguage,
  })
  const exactPlanInPlansData = !!plansData.find((plan) => plan.get('sku') === currentPlan.get('sku'))

  // sort plans for mobile SPC
  if (isMobile && sliderMode && isSingleCheckoutPage) {
    plansData = plansData.sort((a, b) => b.get('bestValue') - a.get('bestValue'))
  }

  if (!isAccountChangePlan && plansList.size === 0) {
    return (
      <div className="plan-card-grid">
        <SherpaMessage message={staticText.get('loadingPlans')} arrow={false} center />
      </div>
    )
  }

  if (planGraphQLActive && isAccountChangePlan && (plansList.size === 0 || !userCurrentPlan.size)) {
    return (
      <div className="plan-card-grid">
        <SherpaMessage message={staticText.get('loadingPlans')} arrow={false} center />
      </div>
    )
  }

  const planCards = plansData.map((plan, index) => {
    // check if the plan is an exact match for the current plan or the next plan
    const planIsExactMatch = plan.get('compositeType') === currentPlan.get('compositeType')
    const nextPlanIsExactMatch = plan.get('compositeType') === nextPlan.get('compositeType')

    return (
      <PlanCard
        {...props}
        key={`plan-card-${index}`}
        plan={plan}
        currentPlan={currentPlan}
        useGraphQLData={planGraphQLActive}
        planClick={() => onChoosePlanClick(plan, currencyIso, { ...props, ...extraProps }, plan.get('sku'))}
        cancellationScheduled={cancellationScheduled}
        sliderMode={sliderMode}
        activeSlide={activeSlide}
        setActiveSlide={setActiveSlide}
        index={index}
        userLanguage={userLanguage}
        userPlanIsExactMatch={planIsExactMatch}
        userNextPlanIsExactMatch={nextPlanIsExactMatch}
      />
    )
  })

  const onSlideChangeFunc = (active) => {
    setActiveSlide(active)
  }

  return (
    <div className={`plan-card-grid${sliderMode ? ' plan-card-grid--slider' : ''}`}>
      {
        isAccountChangePlan && !exactPlanInPlansData && (
          <div className="plan-card-grid__user-current-plan">
            <H2
              as={HEADING_TYPES.H5}
              className="plan-card-grid__user-current-plan-title"
            >
              {staticText.get('currentPlan')}
            </H2>
            <p className="plan-card-grid__user-current-plan-name">{currentPlan.get('title')}</p>
            <p className="plan-card-grid__user-current-plan-description">{currentPlan.get('shortDetails')}</p>
            {
              cancellationScheduled
                ? <Button
                    className="plan-card-grid__user-current-plan-button"
                    onClick={() => onChoosePlanClick(
                      currentPlan,
                      currencyIso,
                      { ...props, ...extraProps },
                      currentPlan.get('sku'),
                    )}
                    type={TYPES.SECONDARY}
                    size={SIZES.XSMALL}
                  >
                    {staticText.get('reactivateMembership')}
                  </Button>
                : null
            }
            <H3
              as={HEADING_TYPES.H5}
              className="plan-card-grid__user-current-plan-title"
            >
              {staticText.get('otherPlans')}
            </H3>
          </div>
        )
      }
      <div className="plan-card-grid__wrapper">
        {
          sliderMode
            ? <Slider
              slides={planCards}
              startIndex={startIndexSlide}
              activeSlide={activeSlide}
              onSlideChange={onSlideChangeFunc}
              enableSlideClick={isMobile}
              isMobile={isMobile}
            />
            : planCards
        }
      </div>
      {
        sliderMode && isMobile
          ? <div className="plan-card-grid__swipe">
              <span className="plan-card-grid__swipe-icon"/>
              <span className="plan-card-grid__swipe-copy">
                {staticText.get('swipe')}
              </span>
              <span className="plan-card-grid__swipe-icon"/>
            </div>
          : null
      }
      {
        taxableCountry ? (
          <div className={`plan-card-grid__taxes${sliderMode ? ' cart-single-page__disclaimer' : ''}`}>
            {pricesPlusTax}
          </div>
        ) : (
          <div className={`plan-card-grid__taxes${sliderMode ? ' cart-single-page__disclaimer' : ''}`}>
            {staticText.get('inclusiveOfTaxes')}
          </div>
        )
      }
    </div>
  )
}

PlanCardGrid.propTypes = {
  history: PropTypes.object.isRequired,
  staticText: ImmutablePropTypes.map.isRequired,
  plans: ImmutablePropTypes.map.isRequired,
  auth: ImmutablePropTypes.map.isRequired,
  user: ImmutablePropTypes.map.isRequired,
  userAccount: ImmutablePropTypes.map.isRequired,
  setPlanSelection: PropTypes.func.isRequired,
  setPlansSelection: PropTypes.func.isRequired,
  setPlansLocalizedSelection: PropTypes.func.isRequired,
  dismissModal: PropTypes.func.isRequired,
  renderModal: PropTypes.func.isRequired,
  setPlanChangeSelected: PropTypes.func.isRequired,
  isCheckoutChangePlan: PropTypes.bool,
  isAccountChangePlan: PropTypes.bool,
  userPlanSubscriptionType: PropTypes.string,
  shouldScrollToTop: PropTypes.bool.isRequired,
  planGridLocation: PropTypes.string.isRequired,
  sliderMode: PropTypes.bool,
  onPlanSelect: PropTypes.func,
  isMobile: PropTypes.bool,
}

PlanCardGrid.defaultProps = {
  shouldScrollToTop: false,
  planGridLocation: BILLING_PLAN_GRID_LOCATION_CHECKOUT,
  sliderMode: false,
  isMobile: false,
}

export default compose(
  connectRedux(
    (state) => ({
      staticText: state.staticText.getIn(['data', 'planCardGrid', 'data']),
      app: state.app,
      plans: state.plans,
      auth: state.auth,
      user: state.user,
      userAccount: state.userAccount,
      path: state.resolver.getIn(['data', 'path']) || '/',
    }),
    (dispatch) => {
      const actions = getBoundActions(dispatch)
      return {
        setPlanSelection: actions.checkout.setPlanSelection,
        setPlansSelection: actions.plans.setPlansSelection,
        setPlansLocalizedSelection: actions.plans.setPlansLocalizedSelection,
        dismissModal: actions.dialog.dismissModal,
        renderModal: actions.dialog.renderModal,
        setPlanChangeSelected: actions.plans.setPlanChangeSelected,
      }
    },
  ),
)(PlanCardGrid)
